import AtriumApi, { getApiErrorMessage } from "api";
import { ErrorState } from "components/states";
import { useAuth } from "hooks/useAuth";
import { useEffect, useState } from "react";
import notify from "lib/notify";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import SettingsSection, { SettingsSectionSkeleton } from "./components/SettingsSection";
import SettingToggleRow, { AutosaveHint } from "./components/SettingToggleRow";

const NotificationSettings = () => {
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const { user } = useAuth();
  const id = user?.id;

  const getUserProfile = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await AtriumApi.get("/v1/users/get-profile");
      if (response?.data?.user) {
        setEmailEnabled(response.data.user.emailNotification ?? true);
        setSmsEnabled(response.data.user.smsNotification ?? true);
      }
    } catch (err) {
      setLoadError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUserProfile();
  }, []);

  const updatePreference = async (field, value) => {
    try {
      await AtriumApi.patch(`/v1/users/update/${id}`, { [field]: value });
      track(EVENTS.SETTINGS_NOTIFICATIONS_SAVED);
      notify.success("Notification preference saved");
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not update notification preference"));
      getUserProfile();
    }
  };

  const handleEmailToggle = (checked) => {
    setEmailEnabled(checked);
    updatePreference("emailNotification", checked);
  };

  const handleSmsToggle = (checked) => {
    setSmsEnabled(checked);
    updatePreference("smsNotification", checked);
  };

  if (loading) return <SettingsSectionSkeleton lines={3} />;

  if (loadError) {
    return (
      <SettingsSection title="Notification Preferences">
        <ErrorState
          message={getApiErrorMessage(loadError, "Could not load notification preferences.")}
          onRetry={getUserProfile}
        />
      </SettingsSection>
    );
  }

  return (
    <SettingsSection
      title="Notification Preferences"
      description="Control how you receive notifications from Atrium."
    >
      <SettingToggleRow
        label="Email Notifications"
        description="Receive surveys, devotionals and digest emails"
        checked={emailEnabled}
        onChange={handleEmailToggle}
      />
      <SettingToggleRow
        label="SMS Notifications"
        description="Receive survey invitations and reminders via text message"
        checked={smsEnabled}
        onChange={handleSmsToggle}
      />
      <AutosaveHint />
      <p className="mt-4 border-t border-line pt-4 text-sm text-fg-muted">
        Account emails such as password resets and staff invitations are always sent, regardless
        of these settings.
      </p>
    </SettingsSection>
  );
};

export default NotificationSettings;
